import { ChangeDetectionStrategy, Component, computed, input, model, resource, signal } from '@angular/core';
import { getClientConfigForm } from 'src/app/forms/superAdminForms';
import { DetailFormDraft, IClientConfigDraft } from 'src/app/models';
import { ClientsService, UtilsService } from 'src/app/services';
import { DetailFormComponent } from 'src/app/shared/detail-form/detail-form.component';
import { FormActionsComponent } from 'src/app/shared/form-actions/form-actions.component';

@Component({
  selector: 'app-client-config-form',
  templateUrl: './client-config-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [DetailFormComponent, FormActionsComponent],
})
export class ClientConfigFormComponent {
  readonly clientId = input.required<number>();
  readonly edit = model(false);

  readonly clientConfig = resource({
    params: () => this.clientId(),
    loader: ({ params: clientId }) => this.clientsService.getClientConfig(clientId),
  });
  readonly loading = computed(() => this.clientConfig.isLoading());
  readonly draft = new DetailFormDraft<IClientConfigDraft>(() => {
    const clientConfig = this.clientConfig.value();
    return {
      maxFaults: clientConfig?.maxFaults ?? null,
      faultWeeksDuration: clientConfig?.faultWeeksDuration ?? null,
      penaltyWeeksDuration: clientConfig?.penaltyWeeksDuration ?? null,
      maxKmsPerMonth: clientConfig?.maxKmsPerMonth ?? null,
      phoneRequired: !!clientConfig?.phoneRequired,
      planningSync: !!clientConfig?.planningSync,
    };
  });
  readonly fields = computed(() => getClientConfigForm(this.draft.value(), this.edit()));
  readonly isValid = computed(() => {
    const draft = this.draft.value();
    const requiredValues = [draft.maxFaults, draft.faultWeeksDuration, draft.penaltyWeeksDuration];
    return (
      requiredValues.every((value) => value !== null && value >= 0) &&
      (draft.maxKmsPerMonth === null || draft.maxKmsPerMonth >= 0)
    );
  });
  readonly saving = signal(false);

  constructor(
    private clientsService: ClientsService,
    private utilsService: UtilsService,
  ) {}

  cancel() {
    this.draft.reset();
    this.edit.set(false);
  }

  async save() {
    if (!this.isValid() || this.saving()) return;
    const draft = this.draft.value();
    this.saving.set(true);
    try {
      const clientConfig = await this.clientsService.updateClientConfig(this.clientId(), {
        maxFaults: draft.maxFaults!,
        faultWeeksDuration: draft.faultWeeksDuration!,
        penaltyWeeksDuration: draft.penaltyWeeksDuration!,
        maxKmsPerMonth: draft.maxKmsPerMonth,
        phoneRequired: draft.phoneRequired,
        planningSync: draft.planningSync,
      });
      this.clientConfig.set(clientConfig);
      this.utilsService.showSuccessNotification('superadminZone.saved');
      this.edit.set(false);
    } finally {
      this.saving.set(false);
    }
  }
}
